import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Inbox, Loader2, ChevronDown, ChevronUp, Package, Truck, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { JobberUnitPicker } from '@/components/processing/JobberUnitPicker';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ProcessingInwardConsignment = () => {
  const navigate = useNavigate();
  const [consignments, setConsignments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [processingUnits, setProcessingUnits] = useState([]);
  const [sections, setSections] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingTotal, setPendingTotal] = useState(0);
  const [receivedTotal, setReceivedTotal] = useState(0);
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showGoodsReadyModal, setShowGoodsReadyModal] = useState(false);
  const [selectedConsignment, setSelectedConsignment] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeTab]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [consignmentsRes, unitsRes, sectionsRes] = await Promise.all([
        apiClient.get(`/processing/inward-consignments?page=${page}&limit=${pageSize}&tab=${activeTab}`),
        apiClient.get('/processing/units'),
        apiClient.get('/internal-transfer/sections?exclude_section=Processing')  // Use public endpoint instead of admin
      ]);
      
      setConsignments(consignmentsRes.data.consignments || []);
      setTotalCount(consignmentsRes.data.total || 0);
      setTotalPages(consignmentsRes.data.total_pages || 1);
      setPendingTotal(consignmentsRes.data.pending_total || 0);
      setReceivedTotal(consignmentsRes.data.received_total || 0);
      setProcessingUnits(unitsRes.data.units || []);
      setSections(sectionsRes.data.sections || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load consignments');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status, transferStatus) => {
    // Handle both processing_status and transfer status
    const effectiveStatus = status || transferStatus;
    
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      awaiting_shipment: { color: 'bg-blue-100 text-blue-800', icon: Package, label: 'Awaiting Shipment' },
      shipment_requested: { color: 'bg-orange-100 text-orange-800', icon: Truck, label: 'Shipment Requested' },
      shipment_confirmed: { color: 'bg-indigo-100 text-indigo-800', icon: Truck, label: 'Shipment Confirmed' },
      received: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Received' },
      completed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle2, label: 'Completed' },
      // Handle internal transfer statuses
      created: { color: 'bg-blue-100 text-blue-800', icon: Package, label: 'Ready to Request' },
      requested: { color: 'bg-orange-100 text-orange-800', icon: Truck, label: 'Requested' },
      shipped: { color: 'bg-indigo-100 text-indigo-800', icon: Truck, label: 'Shipped' },
      ready: { color: 'bg-blue-100 text-blue-800', icon: Package, label: 'Ready' }
    };
    
    const config = statusConfig[effectiveStatus] || { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: effectiveStatus || 'Unknown' };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const handleAssignUnit = (consignment) => {
    setSelectedConsignment(consignment);
    setSelectedUnit(consignment.jo_send_to_type === 'jobber' ? (consignment.jo_jobber_id || '') : '');
    setShowAssignModal(true);
  };

  // Job-order consignments: only the order's jobber, or the jobbers tagged with its category.
  const unitsFor = (c) => {
    if (c?.jo_send_to_type === 'jobber') return processingUnits.filter((u) => u.unit_id === c.jo_jobber_id);
    if (c?.jo_send_to_type === 'category') return processingUnits.filter((u) => (u.categories || []).includes(c.jo_category));
    return processingUnits;
  };
  const modalUnits = unitsFor(selectedConsignment);

  const handleRequestConsignment = async (consignment) => {
    setIsProcessing(true);
    try {
      // For gate entry consignments, use entry_id; for internal transfers, use transfer_id
      const idToUse = consignment.entry_id || consignment.transfer_id;
      await apiClient.post(`/consignments/section/Processing/request?entry_id=${encodeURIComponent(idToUse)}`);
      toast.success('Consignment requested - waiting for source section to ship');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request consignment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAcceptConsignment = async (consignment) => {
    setIsProcessing(true);
    try {
      // For gate entry consignments, use entry_id; for internal transfers, use transfer_id
      const idToUse = consignment.entry_id || consignment.transfer_id;
      await apiClient.post(`/consignments/section/Processing/accept?entry_id=${encodeURIComponent(idToUse)}`);
      toast.success('Consignment accepted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept consignment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRequestShipment = async (consignment) => {
    if (!consignment.processing_unit_id) {
      toast.error('Please assign a processing unit first');
      return;
    }
    
    setIsProcessing(true);
    try {
      await apiClient.post('/processing/request-shipment', {
        consignment_id: consignment.transfer_id,
        processing_unit_id: consignment.processing_unit_id
      });
      toast.success('Shipment request sent to source section');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request shipment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReceiveShipment = async (consignment) => {
    setIsProcessing(true);
    try {
      await apiClient.post('/processing/receive-shipment', {
        consignment_id: consignment.transfer_id
      });
      toast.success('Shipment marked as received');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive shipment');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkProcessingComplete = async (consignment) => {
    setIsProcessing(true);
    try {
      await apiClient.post('/processing/mark-complete', {
        transfer_id: consignment.transfer_id
      });
      toast.success('Processing marked as complete. Bundles stored in inventory.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark processing complete');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoodsReady = (consignment) => {
    setSelectedConsignment(consignment);
    setSelectedDestination('');
    setShowGoodsReadyModal(true);
  };

  const submitAssignment = async () => {
    if (!selectedUnit) {
      toast.error('Please select a processing unit');
      return;
    }
    
    setIsProcessing(true);
    try {
      const response = await apiClient.post('/processing/assign-unit', {
        transfer_id: selectedConsignment.transfer_id,
        processing_unit_id: selectedUnit,
        design_items: Object.entries(selectedConsignment.design_wise_summary || {}).map(([design, data]) => ({
          design_name: design,
          bundle_count: data.count,
          bundles: data.bundles
        }))
      });
      
      const unitName = response.data.unit_name || 'Processing unit';
      const notifiedSection = response.data.notified_section;
      
      toast.success(`${unitName} assigned successfully! ${notifiedSection} has been notified.`);
      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign unit');
    } finally {
      setIsProcessing(false);
    }
  };

  const submitGoodsReady = async () => {
    if (!selectedDestination) {
      toast.error('Please select a destination section');
      return;
    }
    
    setIsProcessing(true);
    try {
      const response = await apiClient.post('/processing/goods-ready', {
        consignment_id: selectedConsignment.transfer_id,
        destination_section: selectedDestination
      });
      toast.success(`Goods ready! New transfer ${response.data.new_transfer_id} created to ${selectedDestination}`);
      setShowGoodsReadyModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark goods ready');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderConsignmentActions = (consignment) => {
    // Check both processing_status and status fields
    const processingStatus = consignment.processing_status;
    const transferStatus = consignment.status;
    const hasUnit = consignment.processing_unit_id || consignment.assigned_unit_id;
    const isFromGateEntry = consignment.source_type === 'inward_documentation';
    
    return (
      <div className="flex flex-wrap gap-2 mt-4">
        {/* For Gate Entry consignments - show Request directly without requiring unit */}
        {isFromGateEntry && !processingStatus && transferStatus === 'created' && (
          <>
            <Button
              size="sm"
              onClick={() => handleRequestConsignment(consignment)}
              disabled={isProcessing}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Truck className="w-4 h-4 mr-1" />
              Request Consignment
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAssignUnit(consignment)}
            >
              <Package className="w-4 h-4 mr-1" />
              Assign Unit (Optional)
            </Button>
          </>
        )}
        
        {/* Step 1: Assign Unit - for newly created internal transfers without unit */}
        {(!isFromGateEntry && !processingStatus && transferStatus === 'created' && !hasUnit) && (
          <Button
            size="sm"
            onClick={() => handleAssignUnit(consignment)}
            className="bg-blue-500 hover:bg-blue-600 text-white"
            data-testid={`assign-unit-btn-${consignment.transfer_id}`}
          >
            <Package className="w-4 h-4 mr-1" />
            Assign Processing Unit
          </Button>
        )}
        
        {/* Step 2: Request Consignment - after unit is assigned (for internal transfers) */}
        {(!isFromGateEntry && !processingStatus && transferStatus === 'created' && hasUnit) && (
          <>
            <span className="text-sm text-green-600 flex items-center mr-2">
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Unit: {consignment.assigned_unit_name || consignment.processing_unit_name}
            </span>
            <Button
              size="sm"
              onClick={() => handleRequestConsignment(consignment)}
              disabled={isProcessing}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Truck className="w-4 h-4 mr-1" />
              Request Consignment
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAssignUnit(consignment)}
            >
              Change Unit
            </Button>
          </>
        )}
        
        {/* Waiting for shipment indicator */}
        {(!processingStatus && transferStatus === 'requested') && (
          <span className="text-sm text-amber-600 flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            Waiting for {consignment.from_section} to ship
            {hasUnit && ` (Unit: ${consignment.assigned_unit_name || consignment.processing_unit_name})`}
          </span>
        )}
        
        {/* Accept/Receive - for shipped transfers */}
        {(!processingStatus && transferStatus === 'shipped') && (
          <Button
            size="sm"
            onClick={() => handleAcceptConsignment(consignment)}
            disabled={isProcessing}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Accept Consignment
          </Button>
        )}
        
        {/* Mark Processing Complete - for received transfers */}
        {(transferStatus === 'received' && !processingStatus) && (
          <Button
            size="sm"
            onClick={() => handleMarkProcessingComplete(consignment)}
            disabled={isProcessing}
            className="bg-purple-500 hover:bg-purple-600 text-white"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Mark Processing Complete
          </Button>
        )}
        
        {/* Processing Completed indicator */}
        {transferStatus === 'processing_completed' && (
          <span className="text-sm text-green-600 flex items-center">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Processing Completed - Ready for Transfer
          </span>
        )}
        
        {/* Assign Unit - available for pending/awaiting_shipment */}
        {['pending', 'awaiting_shipment'].includes(processingStatus) && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAssignUnit(consignment)}
            className="bg-blue-50 hover:bg-blue-100 text-blue-700"
          >
            <Package className="w-4 h-4 mr-1" />
            {consignment.processing_unit_name ? 'Change Unit' : 'Assign Unit'}
          </Button>
        )}
        
        {/* Request Shipment - available after unit assigned */}
        {processingStatus === 'awaiting_shipment' && consignment.processing_unit_id && (
          <Button
            size="sm"
            onClick={() => handleRequestShipment(consignment)}
            disabled={isProcessing}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Truck className="w-4 h-4 mr-1" />
            Request Shipment
          </Button>
        )}
        
        {/* Received Shipment - available after shipment confirmed.
            Also surfaces when the legacy "Request Consignment" handshake
            advanced the transfer-level status to "shipped" (GS Accept on a
            requested transfer, no processing_status flow). */}
        {(processingStatus === 'shipment_confirmed'
          || (transferStatus === 'shipped' && !['received', 'processing_completed'].includes(processingStatus))) && (
          <Button
            size="sm"
            onClick={() => handleReceiveShipment(consignment)}
            disabled={isProcessing}
            className="bg-green-500 hover:bg-green-600 text-white"
            data-testid="confirm-receipt-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Confirm Receipt
          </Button>
        )}
        
        {/* Mark Processing Complete - for processing_status received */}
        {processingStatus === 'received' && (
          <Button
            size="sm"
            onClick={() => handleMarkProcessingComplete(consignment)}
            disabled={isProcessing}
            className="bg-purple-500 hover:bg-purple-600 text-white"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Mark Processing Complete
          </Button>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sections/processing')}
              className="text-white hover:bg-[#2D5F4F]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Inward Consignment</h1>
              <p className="text-sm text-gray-400">Processing Section</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => { setActiveTab('pending'); setPage(1); }}
            data-testid="tab-pending"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'pending' ? 'border-[#2D5F4F] text-[#2D5F4F]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          >
            Pending
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'pending' ? 'bg-[#2D5F4F] text-white' : 'bg-gray-100 text-gray-500'}`} data-testid="tab-pending-count">{pendingTotal}</span>
          </button>
          <button
            onClick={() => { setActiveTab('received'); setPage(1); }}
            data-testid="tab-received"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'received' ? 'border-[#2D5F4F] text-[#2D5F4F]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          >
            Received Slips
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'received' ? 'bg-[#2D5F4F] text-white' : 'bg-gray-100 text-gray-500'}`} data-testid="tab-received-count">{receivedTotal}</span>
          </button>
        </div>
        {consignments.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-8 text-center">
              <Inbox className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">{activeTab === 'received' ? 'No received slips' : 'No pending consignments'}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Header showing total + per-page selector */}
            <div className="flex items-center justify-between mb-4 text-sm">
              <div className="text-gray-600" data-testid="ic-total-count">
                <span className="font-semibold text-gray-800">{totalCount}</span>{' '}
                consignment{totalCount === 1 ? '' : 's'} found
                {totalPages > 1 && (
                  <span className="text-gray-500">
                    {' '}· Showing page {page} of {totalPages}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-gray-600">Per page:</Label>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPage(1);
                    setPageSize(Number(v));
                  }}
                >
                  <SelectTrigger className="w-24 h-9" data-testid="ic-page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              {consignments.map((consignment) => (
              <Card key={consignment.transfer_id} className="bg-white shadow-md">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-lg">
                        {consignment.document_barcode || consignment.entry_id || consignment.transfer_id}
                      </CardTitle>
                      {getStatusBadge(consignment.processing_status, consignment.status)}
                      {consignment.source_type === 'inward_documentation' && (
                        <Badge variant="outline" className="text-xs">Gate Entry</Badge>
                      )}
                      {consignment.jo_id && (
                        <Badge variant="outline" className="text-xs border-sky-300 bg-sky-50 text-sky-800" data-testid={`ic-jo-badge-${consignment.transfer_id}`}>
                          Job order {consignment.jo_id} · {consignment.jo_send_to_type === 'jobber' ? consignment.jo_jobber_name : `any ${consignment.jo_category}`}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedId(expandedId === consignment.transfer_id ? null : consignment.transfer_id)}
                    >
                      {expandedId === consignment.transfer_id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    From: <span className="font-medium">{consignment.from_section}</span> | 
                    Bundles: <span className="font-medium">{consignment.bundle_count}</span>
                    {consignment.usn_count != null && (
                      <> | USNs: <span className="font-medium" data-testid={`usn-count-${consignment.transfer_id || consignment.entry_id}`}>{consignment.usn_count}</span></>
                    )}
                    {consignment.vehicle_no && (
                      <> | Vehicle: <span className="font-medium">{consignment.vehicle_no}</span></>
                    )}
                    {consignment.supplier_name && (
                      <> | Supplier: <span className="font-medium">{consignment.supplier_name}</span></>
                    )}
                    {consignment.processing_unit_name && (
                      <> | Unit: <span className="font-medium text-blue-600">{consignment.processing_unit_name}</span></>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent>
                  {/* Design-wise Summary (collapsed view) */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    {Object.entries(consignment.design_wise_summary || {}).slice(0, 3).map(([design, data]) => (
                      <Badge key={design} variant="outline" className="bg-gray-50">
                        {design}: {data.count} bundles
                      </Badge>
                    ))}
                    {Object.keys(consignment.design_wise_summary || {}).length > 3 && (
                      <Badge variant="outline" className="bg-gray-50">
                        +{Object.keys(consignment.design_wise_summary).length - 3} more
                      </Badge>
                    )}
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedId === consignment.transfer_id && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-gray-700 mb-3">Design-wise List</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(consignment.design_wise_summary || {}).map(([design, data]) => (
                          <div key={design} className="p-3 bg-gray-50 rounded-lg">
                            <p className="font-medium text-gray-800">{design}</p>
                            <p className="text-sm text-gray-500">{data.count} bundles</p>
                          </div>
                        ))}
                      </div>
                      
                      {/* Timeline/History */}
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold text-gray-700 mb-3">History</h4>
                        <div className="space-y-2 text-sm">
                          <p className="text-gray-600">
                            Created: {new Date(consignment.created_at).toLocaleString()}
                          </p>
                          {consignment.processing_assigned_at && (
                            <p className="text-gray-600">
                              Unit Assigned: {new Date(consignment.processing_assigned_at).toLocaleString()}
                            </p>
                          )}
                          {consignment.shipment_requested_at && (
                            <p className="text-gray-600">
                              Shipment Requested: {new Date(consignment.shipment_requested_at).toLocaleString()}
                            </p>
                          )}
                          {consignment.shipment_confirmed_at && (
                            <p className="text-gray-600">
                              Shipment Confirmed: {new Date(consignment.shipment_confirmed_at).toLocaleString()}
                            </p>
                          )}
                          {consignment.received_at && (
                            <p className="text-gray-600">
                              Received: {new Date(consignment.received_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  {renderConsignmentActions(consignment)}
                </CardContent>
              </Card>
            ))}
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div
                className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200"
                data-testid="ic-pagination"
              >
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="ic-page-prev-btn"
                >
                  ← Previous
                </Button>
                <div className="text-sm text-gray-600" data-testid="ic-page-indicator">
                  Page <span className="font-bold text-gray-900">{page}</span> of{' '}
                  <span className="font-bold text-gray-900">{totalPages}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || isLoading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="ic-page-next-btn"
                >
                  Next →
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Assign Processing Unit Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Processing Unit</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Transfer ID</Label>
              <p className="font-mono text-sm bg-gray-100 p-2 rounded">{selectedConsignment?.transfer_id}</p>
            </div>
            <div>
              <Label>Bundles</Label>
              <p className="text-sm text-gray-600">{selectedConsignment?.bundle_count} bundles from {selectedConsignment?.from_section}</p>
            </div>
            <div>
              <Label>Select Processing Unit (Jobber)</Label>
              {selectedConsignment?.jo_id && (
                <p className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1 my-1" data-testid="assign-jo-note">
                  Job order {selectedConsignment.jo_id} — {selectedConsignment.jo_send_to_type === 'jobber'
                    ? `goes to ${selectedConsignment.jo_jobber_name}`
                    : `pick any active ${selectedConsignment.jo_category} jobber`}
                </p>
              )}
              <JobberUnitPicker units={modalUnits} value={selectedUnit} onChange={setSelectedUnit} />
              {modalUnits.length === 0 && (
                <p className="text-xs text-red-500 mt-1" data-testid="processing-unit-empty">
                  {selectedConsignment?.jo_id
                    ? (selectedConsignment.jo_send_to_type === 'jobber'
                      ? `${selectedConsignment.jo_jobber_name} is inactive in the Jobber Master`
                      : `No active jobber is tagged ${selectedConsignment.jo_category} — tag one in Admin → Jobber Master`)
                    : 'No active jobbers. Add them in Admin → Jobber Master'}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button onClick={submitAssignment} disabled={isProcessing || !selectedUnit} data-testid="assign-unit-submit-btn">
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Assign Unit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goods Ready Modal */}
      <Dialog open={showGoodsReadyModal} onOpenChange={setShowGoodsReadyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Goods Ready</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Transfer ID</Label>
              <p className="font-mono text-sm bg-gray-100 p-2 rounded">{selectedConsignment?.transfer_id}</p>
            </div>
            <div>
              <Label>Bundles</Label>
              <p className="text-sm text-gray-600">{selectedConsignment?.bundle_count} bundles ready for transfer</p>
            </div>
            <div>
              <Label>Select Destination Section</Label>
              <Select value={selectedDestination} onValueChange={setSelectedDestination}>
                <SelectTrigger>
                  <SelectValue placeholder="Select destination section" />
                </SelectTrigger>
                <SelectContent>
                  {sections.filter(s => s.section_name?.toLowerCase() !== 'processing').map((section) => (
                    <SelectItem key={section.section_id || section.section_name} value={section.section_name}>
                      {section.section_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                This will create a new Internal Transfer to the selected section and notify them of the incoming goods.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGoodsReadyModal(false)}>Cancel</Button>
            <Button onClick={submitGoodsReady} disabled={isProcessing || !selectedDestination} className="bg-purple-600 hover:bg-purple-700">
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm & Create Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcessingInwardConsignment;
